import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { buildV3Location, getStableRouteKey } from '../locationUtils'
import GetComponentRoute from './GetComponentRoute'
import RouteLoadFallback from './RouteLoadFallback'

/**
 * Resolves a v3 getIndexRoute callback, then renders the returned route definition.
 * getIndexRoute is invoked on every navigation so side effects (setSubroute, setPageTitle)
 * stay in sync with the URL — results are not cached via loadRouteOnce.
 */
export default function GetIndexRouteLoader({ getIndexRoute, routeScope }) {
  const location = useLocation()
  const params = useParams()
  const [indexRoute, setIndexRoute] = useState(null)
  const [error, setError] = useState(null)
  const getIndexRouteRef = useRef(getIndexRoute)
  getIndexRouteRef.current = getIndexRoute
  const routeKey = getStableRouteKey(location)
  const loadKey = `${routeScope ?? 'route'}:${routeKey}`
  const resolvedLoadKeyRef = useRef(null)
  const prevLoadKeyRef = useRef(null)

  useEffect(() => {
    const resolveIndexRoute = getIndexRouteRef.current
    if (!resolveIndexRoute) return undefined

    let cancelled = false
    setError(null)

    if (prevLoadKeyRef.current !== loadKey) {
      setIndexRoute(null)
      resolvedLoadKeyRef.current = null
      prevLoadKeyRef.current = loadKey
    }

    const v3Location = buildV3Location(location, params)

    new Promise((resolve, reject) => {
      resolveIndexRoute(v3Location, (err, route) => {
        if (err) reject(err)
        else resolve(route || null)
      })
    })
      .then(route => {
        if (!cancelled) {
          resolvedLoadKeyRef.current = loadKey
          setIndexRoute(route)
        }
      })
      .catch(err => {
        if (!cancelled) setError(err)
      })

    return () => {
      cancelled = true
    }
  }, [loadKey, routeKey, routeScope])

  if (error) throw error
  if (!indexRoute || resolvedLoadKeyRef.current !== loadKey) {
    return <RouteLoadFallback />
  }

  if (indexRoute.getComponent) {
    return (
      <GetComponentRoute
        key={loadKey}
        getComponent={indexRoute.getComponent}
        routeScope={routeScope}
      />
    )
  }

  if (indexRoute.component) {
    const IndexComponent = indexRoute.component
    const v3Location = buildV3Location(location, params)
    return <IndexComponent location={v3Location} params={params} />
  }

  return <RouteLoadFallback />
}
